import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaNueveM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  // small helper to wrap & draw text, returns last y
  const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
    if (!text) return y;
    const words = String(text).split(/\s+/);
    let line = '';
    for (let n = 0; n < words.length; n++) {
      const testLine = line ? (line + ' ' + words[n]) : words[n];
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        ctx.fillText(line, x, y);
        line = words[n];
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, x, y);
      y += lineHeight;
    }
    return y;
  };

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const padding = 24;
      let y = padding;

      // left title square
      const titleFontSize = opts.titleFontSize || 45;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 1.5);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 25;
      const squareText = props.titleSquareText || '1.7';

      ctx.fillStyle = opts.titleSquareBg || '#f1f891ff';
      ctx.fillRect(padding, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#111111ff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, padding + squareSize / 2, y + squareSize / 2);

      // title (centered on page)
      const title = opts.title || props.title || 'Advertencias de posibles riesgos';
      const subTitle = opts.subTitle || props.subTitle || '';
      const texto = opts.texto || props.texto || '';
      const titleX = Math.round(width / 2);
      const titleMaxW = Math.min(typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : width - padding * 2, width - padding * 2);
      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // draw title with wrapping helper
      const titleLineHeight = Math.round(titleFontSize * 1.05);
      const drawWrapped = (context, text, x, startY, maxW, lh) => {
        if (!text) return startY;
        const words = String(text).split(/\s+/);
        let line = '';
        let curY = startY;
        for (let i = 0; i < words.length; i++) {
          const test = line ? (line + ' ' + words[i]) : words[i];
          if (context.measureText(test).width > maxW && line) {
            context.fillText(line, x, curY);
            line = words[i];
            curY += lh;
          } else {
            line = test;
          }
        }
        if (line) {
          context.fillText(line, x, curY);
          curY += lh;
        }
        return curY;
      };

      const titleYStart = y;
      y = drawWrapped(ctx, title, titleX, titleYStart, titleMaxW, titleLineHeight);

      // leave gap after title before box
      y += 200;

      // subtitle drawn above the box; blue bordered box contains texto and recomendaciones boxes side-by-side
      if (subTitle || texto || props.recomendaciones) {
        // increase usable inner width (was padding*8 making boxes too narrow)
        const boxInnerMaxW = Math.min(width - padding * 2, 1400);

        // helper to split paragraphs/newlines and wrap into lines with metadata for justification
        const wrapLines = (context, txt, maxW) => {
          if (!txt) return [];
          const spaceW = context.measureText(' ').width;
          return String(txt)
            .split(/\r?\n/) // paragraphs by explicit newline
            .map((paragraph, pIdx, arr) => {
              if (!paragraph) return [{ words: [''], wordsWidth: 0, isLastOfParagraph: true }];
              const words = paragraph.split(/\s+/).filter(Boolean);
              const lines = [];
              let cur = [];
              let curW = 0;
              for (let w of words) {
                const ww = context.measureText(w).width;
                if (cur.length === 0) {
                  cur.push(w); curW = ww;
                } else if (curW + spaceW + ww <= maxW) {
                  cur.push(w); curW += spaceW + ww;
                } else {
                  lines.push({ words: cur.slice(), wordsWidth: curW, isLastOfParagraph: false });
                  cur = [w]; curW = ww;
                }
              }
              if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW, isLastOfParagraph: true });
              return lines;
            })
            .flat();
        };

        // subtitle lines (drawn in blue box above the two content boxes)
        const subFontSize = opts.subTitleFontSize || 40;
        ctx.font = `600 ${subFontSize}px Inter, Arial, sans-serif`;
        const subLineH = Math.round(subFontSize * 1.25);
        const subLines = wrapLines(ctx, subTitle, boxInnerMaxW);

        // prepare texto lines (left box)
        const bodyFontSize = opts.textoFontSize || 25;
        ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
        const bodyLineH = Math.round(bodyFontSize * 1.4);
        const textLines = wrapLines(ctx, texto, boxInnerMaxW);

        // prepare recomendaciones (right box) - accept string or array
        const rawRec = opts.recomendaciones || props.recomendaciones || '';
        const recItems = Array.isArray(rawRec)
          ? rawRec
          : (rawRec ? String(rawRec).split(/\r?\n/).map(s => s.trim()).filter(Boolean) : []);
        // combine rec items into wrapped lines for the box
        const recWrapped = recItems.map(item => wrapLines(ctx, item, boxInnerMaxW)).flat();

        // determine whether to place boxes side-by-side or stacked
        const gapBetweenBoxes = 24;
        const totalBoxesW = Math.min(boxInnerMaxW, width - padding * 2);
        const sideBySide = totalBoxesW >= 520; // threshold — adjust if desired

        const vPadding = 18;
        const hPadding = 16;

        // Now draw the two boxes
        if (sideBySide) {
          const boxW = Math.floor((totalBoxesW - gapBetweenBoxes) / 2);
          // left box (texto)
          const leftBoxW = Math.floor((totalBoxesW - gapBetweenBoxes) * 0.5);
          const leftContentW = leftBoxW - hPadding * 2;
          // compute wrapped lines for left with actual content width
          const leftLines = wrapLines(ctx, texto, leftContentW);
          const leftContentHeight = leftLines.length * bodyLineH;

          // right box (recomendaciones)
          const rightBoxW = (totalBoxesW - gapBetweenBoxes) - leftBoxW;
          const rightContentW = rightBoxW - hPadding * 2;
          // wrap rec items to lines with rightContentW
          const rightLines = [];
          for (let it of recItems) {
            const wrapped = wrapLines(ctx, it, rightContentW);
            if (wrapped.length === 0) wrapped.push({ words: [''], wordsWidth: 0, isLastOfParagraph: true });
            // add a blank line between numbered items visually
            rightLines.push(...wrapped, { words: [''], wordsWidth: 0, isLastOfParagraph: true });
          }
          // remove trailing blank
          if (rightLines.length > 0 && rightLines[rightLines.length - 1].words.length === 1 && rightLines[rightLines.length - 1].words[0] === '') rightLines.pop();
          const rightContentHeight = rightLines.length * bodyLineH;

          const contentHeight = Math.max(leftContentHeight, rightContentHeight);
          const boxHeight = contentHeight + vPadding * 2;

          // center the two boxes within totalBoxesW
          const baseX = Math.round((width - totalBoxesW) / 2);
          const leftX = baseX;
          const rightX = baseX + leftBoxW + gapBetweenBoxes;
          const boxY = Math.max(y, padding + squareSize + 12);
          const r = 8;

          // small blue label above the texto (left) box (side-by-side)
          const textoTitle = opts.subTitle || props.subTitle || 'Descripción';
          if (textoTitle) {
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            const tPadX = 12;
            const tPadY = 6;
            const measuredT = ctx.measureText(textoTitle).width;
            const tBoxW = Math.min(leftBoxW, Math.round(measuredT + tPadX * 2));
            const tBoxH = Math.round(Math.max(bodyLineH * 0.9, 16)) + tPadY * 2;
            const tBoxX = leftX + Math.round((leftBoxW - tBoxW) / 2);
            const tBoxY = Math.max(padding, boxY - tBoxH - 8);
            const tr = 6;
            ctx.beginPath();
            ctx.moveTo(tBoxX + tr, tBoxY);
            ctx.lineTo(tBoxX + tBoxW - tr, tBoxY);
            ctx.quadraticCurveTo(tBoxX + tBoxW, tBoxY, tBoxX + tBoxW, tBoxY + tr);
            ctx.lineTo(tBoxX + tBoxW, tBoxY + tBoxH - tr);
            ctx.quadraticCurveTo(tBoxX + tBoxW, tBoxY + tBoxH, tBoxX + tBoxW - tr, tBoxY + tBoxH);
            ctx.lineTo(tBoxX + tr, tBoxY + tBoxH);
            ctx.quadraticCurveTo(tBoxX, tBoxY + tBoxH, tBoxX, tBoxY + tBoxH - tr);
            ctx.lineTo(tBoxX, tBoxY + tr);
            ctx.quadraticCurveTo(tBoxX, tBoxY, tBoxX + tr, tBoxY);
            ctx.closePath();
            ctx.fillStyle = opts.subBoxFill || '#2b6cb0';
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = opts.subBoxBorderColor || '#1e40af';
            ctx.stroke();
            ctx.fillStyle = opts.subTitleColor || '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            ctx.fillText(textoTitle, tBoxX + tBoxW / 2, tBoxY + tBoxH / 2);
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
          }

          // small blue label above the recomendaciones box (side-by-side)
          const recTitle = opts.recomendacionesTitle || 'Recomendaciones';
          if (recItems && recItems.length) {
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            const recPadX = 12;
            const recPadY = 6;
            const measured = ctx.measureText(recTitle).width;
            // width limited to right box width
            const recBoxW = Math.min(rightBoxW, Math.round(measured + recPadX * 2));
            const recBoxH = Math.round(Math.max(bodyLineH * 0.9, 16)) + recPadY * 2;
            // center above right box
            const recBoxX = rightX + Math.round((rightBoxW - recBoxW) / 2);
            // place slightly above the main box top, but clamp to padding
            const recBoxY = Math.max(padding, boxY - recBoxH - 8);
            const rr = 6;
            // draw rounded blue box
            ctx.beginPath();
            ctx.moveTo(recBoxX + rr, recBoxY);
            ctx.lineTo(recBoxX + recBoxW - rr, recBoxY);
            ctx.quadraticCurveTo(recBoxX + recBoxW, recBoxY, recBoxX + recBoxW, recBoxY + rr);
            ctx.lineTo(recBoxX + recBoxW, recBoxY + recBoxH - rr);
            ctx.quadraticCurveTo(recBoxX + recBoxW, recBoxY + recBoxH, recBoxX + recBoxW - rr, recBoxY + recBoxH);
            ctx.lineTo(recBoxX + rr, recBoxY + recBoxH);
            ctx.quadraticCurveTo(recBoxX, recBoxY + recBoxH, recBoxX, recBoxY + recBoxH - rr);
            ctx.lineTo(recBoxX, recBoxY + rr);
            ctx.quadraticCurveTo(recBoxX, recBoxY, recBoxX + rr, recBoxY);
            ctx.closePath();
            ctx.fillStyle = opts.subBoxFill || '#2b6cb0';
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = opts.subBoxBorderColor || '#1e40af';
            ctx.stroke();
            // title text (white) centered
            ctx.fillStyle = opts.subTitleColor || '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            ctx.fillText(recTitle, recBoxX + recBoxW / 2, recBoxY + recBoxH / 2);
            // restore baseline & align for subsequent box text
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
          }

          // rounded left box
          ctx.beginPath();
          ctx.moveTo(leftX + r, boxY);
          ctx.lineTo(leftX + leftBoxW - r, boxY);
          ctx.quadraticCurveTo(leftX + leftBoxW, boxY, leftX + leftBoxW, boxY + r);
          ctx.lineTo(leftX + leftBoxW, boxY + boxHeight - r);
          ctx.quadraticCurveTo(leftX + leftBoxW, boxY + boxHeight, leftX + leftBoxW - r, boxY + boxHeight);
          ctx.lineTo(leftX + r, boxY + boxHeight);
          ctx.quadraticCurveTo(leftX, boxY + boxHeight, leftX, boxY + boxHeight - r);
          ctx.lineTo(leftX, boxY + r);
          ctx.quadraticCurveTo(leftX, boxY, leftX + r, boxY);
          ctx.closePath();
          ctx.fillStyle = opts.boxFill || 'rgba(255,255,255,0.95)';
          ctx.fill();
          ctx.lineWidth = opts.boxBorderWidth || 2;
          ctx.strokeStyle = opts.boxBorderColor || '#2b6cb0';
          ctx.stroke();

          // rounded right box
          ctx.beginPath();
          ctx.moveTo(rightX + r, boxY);
          ctx.lineTo(rightX + rightBoxW - r, boxY);
          ctx.quadraticCurveTo(rightX + rightBoxW, boxY, rightX + rightBoxW, boxY + r);
          ctx.lineTo(rightX + rightBoxW, boxY + boxHeight - r);
          ctx.quadraticCurveTo(rightX + rightBoxW, boxY + boxHeight, rightX + rightBoxW - r, boxY + boxHeight);
          ctx.lineTo(rightX + r, boxY + boxHeight);
          ctx.quadraticCurveTo(rightX, boxY + boxHeight, rightX, boxY + boxHeight - r);
          ctx.lineTo(rightX, boxY + r);
          ctx.quadraticCurveTo(rightX, boxY, rightX + r, boxY);
          ctx.closePath();
          ctx.fillStyle = opts.boxFill || 'rgba(255,255,255,0.95)';
          ctx.fill();
          ctx.lineWidth = opts.boxBorderWidth || 2;
          ctx.strokeStyle = opts.boxBorderColor || '#2b6cb0';
          ctx.stroke();

          // draw left text (leftLines) with justification
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillStyle = opts.textoColor || '#0f172a';
          ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
          let curY = boxY + vPadding;
          const startX = leftX + hPadding;
          const spaceW = ctx.measureText(' ').width;
          const maxLeftLines = wrapLines(ctx, texto, leftContentW);
          for (let ln of maxLeftLines) {
            if (!ln.isLastOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (leftContentW - ln.wordsWidth) / gaps;
              let cx = startX;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, curY);
                cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), startX, curY);
            }
            curY += bodyLineH;
          }

          // draw right text (rightLines) with justification
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillStyle = opts.textoColor || '#0f172a';
          ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
          curY = boxY + vPadding;
          const startXRight = rightX + hPadding;
          const spaceWRight = ctx.measureText(' ').width;
          for (let ln of rightLines) {
            if (!ln.isLastOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (rightContentW - ln.wordsWidth) / gaps;
              let cx = startXRight;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, curY);
                cx += ctx.measureText(ln.words[j]).width + spaceWRight + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), startXRight, curY);
            }
            curY += bodyLineH;
          }

          y = boxY + boxHeight + 12;
        } else {
          // stacked mode — draw first box centered (texto), then recomendaciones below it
          const contentW = totalBoxesW;
          const boxW = contentW;
          const contentInnerW = boxW - hPadding * 2;
          const leftLines = wrapLines(ctx, texto, contentInnerW);
          const leftContentHeight = leftLines.length * bodyLineH;
          const rightLines = [];
          for (let it of recItems) {
            const wrapped = wrapLines(ctx, it, contentInnerW);
            if (wrapped.length === 0) wrapped.push({ words: [''], wordsWidth: 0, isLastOfParagraph: true });
            rightLines.push(...wrapped, { words: [''], wordsWidth: 0, isLastOfParagraph: true });
          }
          if (rightLines.length && rightLines[rightLines.length - 1].words.length === 1 && rightLines[rightLines.length - 1].words[0] === '') rightLines.pop();
          const rightContentHeight = rightLines.length * bodyLineH;
          const boxHeightLeft = leftContentHeight + vPadding * 2;
          const boxHeightRight = rightContentHeight + vPadding * 2;

          const boxX = Math.round((width - boxW) / 2);
          const boxY = Math.max(y, padding + squareSize + 12);
          const r = 8;

          // small blue label above the texto (left) box (stacked)
          const textoTitle = opts.textoTitle || props.textoTitle || 'Descripción';
          if (textoTitle) {
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            const tPadX = 12;
            const tPadY = 6;
            const measuredT = ctx.measureText(textoTitle).width;
            const tBoxW = Math.min(boxW, Math.round(measuredT + tPadX * 2));
            const tBoxH = Math.round(Math.max(bodyLineH * 0.9, 16)) + tPadY * 2;
            const tBoxX = boxX + Math.round((boxW - tBoxW) / 2);
            const tBoxY = Math.max(padding, boxY - tBoxH - 8);
            const tr = 6;
            ctx.beginPath();
            ctx.moveTo(tBoxX + tr, tBoxY);
            ctx.lineTo(tBoxX + tBoxW - tr, tBoxY);
            ctx.quadraticCurveTo(tBoxX + tBoxW, tBoxY, tBoxX + tBoxW, tBoxY + tr);
            ctx.lineTo(tBoxX + tBoxW, tBoxY + tBoxH - tr);
            ctx.quadraticCurveTo(tBoxX + tBoxW, tBoxY + tBoxH, tBoxX + tBoxW - tr, tBoxY + tBoxH);
            ctx.lineTo(tBoxX + tr, tBoxY + tBoxH);
            ctx.quadraticCurveTo(tBoxX, tBoxY + tBoxH, tBoxX, tBoxY + tBoxH - tr);
            ctx.lineTo(tBoxX, tBoxY + tr);
            ctx.quadraticCurveTo(tBoxX, tBoxY, tBoxX + tr, tBoxY);
            ctx.closePath();
            ctx.fillStyle = opts.subBoxFill || '#2b6cb0';
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = opts.subBoxBorderColor || '#1e40af';
            ctx.stroke();
            ctx.fillStyle = opts.subTitleColor || '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            ctx.fillText(textoTitle, tBoxX + tBoxW / 2, tBoxY + tBoxH / 2);
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
          }

          // left (texto) box
          ctx.beginPath();
          ctx.moveTo(boxX + r, boxY);
          ctx.lineTo(boxX + boxW - r, boxY);
          ctx.quadraticCurveTo(boxX + boxW, boxY, boxX + boxW, boxY + r);
          ctx.lineTo(boxX + boxW, boxY + boxHeightLeft - r);
          ctx.quadraticCurveTo(boxX + boxW, boxY + boxHeightLeft, boxX + boxW - r, boxY + boxHeightLeft);
          ctx.lineTo(boxX + r, boxY + boxHeightLeft);
          ctx.quadraticCurveTo(boxX, boxY + boxHeightLeft, boxX, boxY + boxHeightLeft - r);
          ctx.lineTo(boxX, boxY + r);
          ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
          ctx.closePath();
          ctx.fillStyle = opts.boxFill || 'rgba(255,255,255,0.95)';
          ctx.fill();
          ctx.lineWidth = opts.boxBorderWidth || 2;
          ctx.strokeStyle = opts.boxBorderColor || '#2b6cb0';
          ctx.stroke();

          // left text with justification
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillStyle = opts.textoColor || '#0f172a';
          ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
          let curY = boxY + vPadding;
          const startX = boxX + hPadding;
          const spaceW = ctx.measureText(' ').width;
          for (let ln of leftLines) {
            if (!ln.isLastOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (contentInnerW - ln.wordsWidth) / gaps;
              let cx = startX;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, curY);
                cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), startX, curY);
            }
            curY += bodyLineH;
          }

          // right (recomendaciones) box below
          const boxY2 = boxY + boxHeightLeft + 12;

          // small blue label above the recomendaciones box (stacked)
          const recTitle = opts.recomendacionesTitle || 'Recomendaciones';
          if (recItems && recItems.length) {
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            const recPadX = 12;
            const recPadY = 6;
            const measured = ctx.measureText(recTitle).width;
            const recBoxW = Math.min(boxW, Math.round(measured + recPadX * 2));
            const recBoxH = Math.round(Math.max(bodyLineH * 0.9, 16)) + recPadY * 2;
            const recBoxX = boxX + Math.round((boxW - recBoxW) / 2);
            const recBoxY = Math.max(padding, boxY2 - recBoxH - 8);
            const rr = 6;
            ctx.beginPath();
            ctx.moveTo(recBoxX + rr, recBoxY);
            ctx.lineTo(recBoxX + recBoxW - rr, recBoxY);
            ctx.quadraticCurveTo(recBoxX + recBoxW, recBoxY, recBoxX + recBoxW, recBoxY + rr);
            ctx.lineTo(recBoxX + recBoxW, recBoxY + recBoxH - rr);
            ctx.quadraticCurveTo(recBoxX + recBoxW, recBoxY + recBoxH, recBoxX + recBoxW - rr, recBoxY + recBoxH);
            ctx.lineTo(recBoxX + rr, recBoxY + recBoxH);
            ctx.quadraticCurveTo(recBoxX, recBoxY + recBoxH, recBoxX, recBoxY + recBoxH - rr);
            ctx.lineTo(recBoxX, recBoxY + rr);
            ctx.quadraticCurveTo(recBoxX, recBoxY, recBoxX + rr, recBoxY);
            ctx.closePath();
            ctx.fillStyle = opts.subBoxFill || '#2b6cb0';
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = opts.subBoxBorderColor || '#1e40af';
            ctx.stroke();
            ctx.fillStyle = opts.subTitleColor || '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `600 ${Math.round(bodyFontSize * 0.9)}px Inter, Arial, sans-serif`;
            ctx.fillText(recTitle, recBoxX + recBoxW / 2, recBoxY + recBoxH / 2);
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
          }

          ctx.beginPath();
          ctx.moveTo(boxX + r, boxY2);
          ctx.lineTo(boxX + boxW - r, boxY2);
          ctx.quadraticCurveTo(boxX + boxW, boxY2, boxX + boxW, boxY2 + r);
          ctx.lineTo(boxX + boxW, boxY2 + boxHeightRight - r);
          ctx.quadraticCurveTo(boxX + boxW, boxY2 + boxHeightRight, boxX + boxW - r, boxY2 + boxHeightRight);
          ctx.lineTo(boxX + r, boxY2 + boxHeightRight);
          ctx.quadraticCurveTo(boxX, boxY2 + boxHeightRight, boxX, boxY2 + boxHeightRight - r);
          ctx.lineTo(boxX, boxY2 + r);
          ctx.quadraticCurveTo(boxX, boxY2, boxX + r, boxY2);
          ctx.closePath();
          ctx.fillStyle = opts.boxFill || 'rgba(255,255,255,0.95)';
          ctx.fill();
          ctx.lineWidth = opts.boxBorderWidth || 2;
          ctx.strokeStyle = opts.boxBorderColor || '#2b6cb0';
          ctx.stroke();

          // right text with justification
          ctx.textAlign = 'left';
          ctx.font = `${bodyFontSize}px Inter, Arial, sans-serif`;
          curY = boxY2 + vPadding;
          const startX2 = boxX + hPadding;
          for (let ln of rightLines) {
            if (!ln.isLastOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (contentInnerW - ln.wordsWidth) / gaps;
              let cx = startX2;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, curY);
                cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), startX2, curY);
            }
            curY += bodyLineH;
          }

          y = boxY2 + boxHeightRight + 12;
        }
      }

      // optional logo (top-right)
      const mainLogoSrc = opts.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(140, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = padding;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_nueve_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});